import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { defaultHome } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

function formatErr(d) {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(", ");
    return d.msg || JSON.stringify(d);
}

export default function AcceptInvite() {
    const [params] = useSearchParams();
    const token = useMemo(() => params.get("token") || "", [params]);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [busy, setBusy] = useState(false);
    const { setUser } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (!token) {
            toast.error("Missing invite token — ask your salon owner for a new link");
            return;
        }
        if (password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        if (password !== confirm) {
            toast.error("Passwords do not match");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/auth/accept-invite", { token, password });
            setUser(data);
            toast.success("Welcome to the team");
            navigate(`/app/${defaultHome(data.role)}`, { replace: true });
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
            <div className="w-full max-w-md rounded-2xl border border-[#E8E4DE] bg-white p-8 shadow-[0_12px_40px_rgba(45,41,38,0.08)]">
                <div className="mb-6 text-center">
                    <BrandLogo
                        size={36}
                        textClass="font-serif text-2xl font-semibold text-[#4A0E17]"
                        accentClass="text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A227]"
                    />
                    <h1 className="mt-4 font-serif text-xl text-[#2D2926]">Join your salon team</h1>
                    <p className="mt-1 text-sm text-[#8A8178]">Set a password to activate your account</p>
                </div>

                {!token ? (
                    <p className="text-center text-sm text-[#C84B31]">
                        This invite link is invalid. Contact your salon owner for a new one.
                    </p>
                ) : (
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                autoComplete="new-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="At least 8 characters"
                                data-testid="invite-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm">Confirm password</Label>
                            <Input
                                id="confirm"
                                type="password"
                                autoComplete="new-password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                data-testid="invite-password-confirm"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-[#4A0E17] hover:bg-[#3a0b12]"
                            disabled={busy}
                            data-testid="invite-submit"
                        >
                            {busy ? "Activating…" : "Activate account"}
                        </Button>
                    </form>
                )}

                <p className="mt-6 text-center text-xs text-[#8A8178]">
                    Already have an account?{" "}
                    <Link to="/login" className="font-semibold text-[#4A0E17] hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
